import { Box, BoxProps, Card, Stack, styled } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { ArchivedDevice, Device } from "@services/devices/types";
import React, { useEffect, useState, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import { getModelLogo } from "@common/utils/devices/logo";
import { DeviceStatusType, DeviceType, getStatusSxColorFn } from "./const";
import { CatalogContext } from "@common/components/CatalogProvider";
import DeviceCardFooter from "./DeviceCardFooter";
import DeviceCardHeader from "./DeviceCardHeader";
import DeviceCardImage from "./DeviceCardImage";
import DeviceCardInformations from "./DeviceCardInformations";
import { useTranslation } from "react-i18next";
import { RoomsRealtimeContext } from "../../Layout/context";
import ArchivedDeviceDrawer from "@pages/Assets/common/ArchivedDevice/Drawers";
import { DeviceProvisioningDispatchContext } from "@pages/Assets/DeviceProvisioning/context";

export type DeviceType = Device | ArchivedDevice;

type DeviceCardProps = {
  device: DeviceType;
  previousPage: string;
  isArchivedDevice: boolean;
};

const isArchivedDevice = (x: DeviceType): x is ArchivedDevice =>
  (x as ArchivedDevice).deletedTime !== undefined;

const isDevice = (x: DeviceType): x is Device => !isArchivedDevice(x);

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  width: "100%",
  color: "unset",
  height: "100%",
}));

const StyledBox = styled(({ type, ...props }: BoxProps & { type?: string }) => (
  <Box {...props} />
))(({ theme, type }) => [
  {
    height: "100%",
    "& .MuiPaper-root": {
      height: "100%",
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${
        DeviceType.ASSET === type
          ? theme.palette.background.highlighter
          : theme.palette.device.border
      }`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      alignItems: "center",
      "&.MuiCard-root": {
        backgroundImage: "unset",
      },
      "& .MuiCardContent-root": {
        alignSelf: "start",
      },
    },
  },
]);

const DeviceCard = (props: DeviceCardProps) => {
  const { previousPage, device, isArchivedDevice } = props;
  const { model, type, category, id, manufacturer, name } = device;
  const [image, setImage] = useState<string>();
  const globalCatalog = useContext(CatalogContext);
  const { detail } = useContext(RoomsRealtimeContext);
  const { setOpen } = useContext(DeviceProvisioningDispatchContext);
  const { t } = useTranslation();
  const [openDrawer, setOpenDrawer] = useState(false);

  const getStatus = (): [DeviceStatusType, string] => {
    if (isDevice(device) && device.status?.inCall)
      return ["meeting", type ?? ""];
    if (device.type === DeviceType.ASSET)
      return [DeviceType.ASSET as DeviceStatusType, type ?? ""];
    return ["default", type ?? ""];
  };
  const [status, label] = getStatus();

  const loadImage = async (logo?: string) => {
    try {
      if (logo) {
        setImage(__symphony_env__.REACT_APP_SERVER_URL + logo);
      }
    } catch (e) {
      console.log(e);
    }
  };
  const setModelLogo = useCallback(() => {
    setImage(
      getModelLogo(
        __symphony_env__.REACT_APP_SERVER_URL,
        { type, category, model },
        globalCatalog
      )
    );
  }, [type, category, model, globalCatalog]);
  useEffect(() => {
    if (isDevice(device) && device.logo) {
      loadImage(device.logo);
    } else {
      if (globalCatalog?.deviceModelDetails) setModelLogo();
    }
  }, [device]);
  useEffect(() => {
    if (isDevice(device) && !device.logo && globalCatalog?.deviceModelDetails) {
      setModelLogo();
    }
  }, [globalCatalog]);

  const handleResetDevice = () => {
    detail?.resetDevice();
  };

  const renderDeviceCard = useCallback(() => {
    if (!isDevice(device)) {
      return (
        <Box
          sx={{
            "&:hover": {
              cursor: "pointer",
            },
          }}
          onClick={() => setOpenDrawer(true)}
        >
          <Stack
            sx={{
              alignItems: "center",
            }}
          >
            <DeviceCardImage image={image} />
          </Stack>
          <DeviceCardInformations
            name={name}
            label1={manufacturer}
            label2={model}
          />
        </Box>
      );
    } else {
      return (
        <Box sx={{ maxWidth: "100%" }}>
          {device.provisioned ? (
            <StyledLink
              onClick={handleResetDevice}
              to={"/assets/devices/" + id}
              state={{ previous: previousPage }}
            >
              <Stack
                sx={{
                  alignItems: "center",
                }}
              >
                <DeviceCardImage image={image} />
              </Stack>
              <DeviceCardInformations
                name={name}
                label1={manufacturer}
                label2={model}
              />
            </StyledLink>
          ) : (
            // To be remove when feature implemented
            (<Box
              sx={{
                "&:hover": {
                  cursor: "pointer",
                },
              }}
              onClick={() => device?.id && setOpen(device.id)}
            >
              <Stack
                sx={{
                  alignItems: "center",
                }}
              >
                <DeviceCardImage image={image} />
              </Stack>
              <DeviceCardInformations
                name={name}
                label1={manufacturer}
                label2={model}
              />
            </Box>)
          )}
        </Box>
      );
    }
  }, [device, isArchivedDevice, image]);

  return (
    <>
      <StyledBox
        type={device.type}
        sx={{
          "& .MuiPaper-root": {
            borderColor: getStatusSxColorFn(status),
          },
        }}
      >
        <Card>
          <Grid
            container
            direction="column"
            sx={{ width: '100%', flexGrow: "1", }}
          >
            <DeviceCardHeader
              device={
                isDevice(device)
                  ? { ...device, room: device.room ?? detail?.room }
                  : undefined
              }
              previousPage={previousPage}
              deviceId={id}
              isArchivedDevice={isArchivedDevice}
            />
            {renderDeviceCard()}
          </Grid>
          <DeviceCardFooter label={label} status={status} />
        </Card>
      </StyledBox>
      <ArchivedDeviceDrawer
        openDrawer={openDrawer}
        setOpenDrawer={setOpenDrawer}
        device={!isDevice(device) ? device : undefined}
      />
    </>
  );
};

export default DeviceCard;
